import { useState, useEffect } from 'react';
import { BarChart3, Euro, FileText, CheckSquare, ClipboardCheck, Building2, UserCheck, Calendar, ArrowUp, ArrowDown, Filter, Search } from 'lucide-react';
import { Collab, Influencer, collabsRef, influencersRef, getDocuments, updateDocument } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';

// Composant pour le graphique de CA mensuel
const MonthlyRevenueChart = ({ collabs, timeRange, startDate, endDate }: { 
  collabs: Collab[], 
  timeRange: 'current' | 'all' | '3months' | 'custom',
  startDate: string,
  endDate: string
}) => {
  const { theme } = useTheme();

  // Calculer le CA par mois
  const getMonthlyRevenue = () => {
    // Créer un objet pour stocker les données mensuelles
    let monthlyData: { [key: string]: number } = {};
    const now = new Date();

    // Déterminer la plage de dates à afficher en fonction du filtre actuel
    let startMonth: Date;
    let endMonth: Date = new Date(now.getFullYear(), now.getMonth(), 1); // Mois actuel par défaut
    
    switch (timeRange) {
      case 'current':
        // Seulement le mois actuel
        startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case '3months':
        // 3 derniers mois
        startMonth = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case 'custom':
        if (startDate) {
          const customStart = new Date(startDate);
          startMonth = new Date(customStart.getFullYear(), customStart.getMonth(), 1); // Premier jour du mois de début
          
          if (endDate) {
            const customEnd = new Date(endDate);
            // Utiliser le mois de fin spécifié
            endMonth = new Date(customEnd.getFullYear(), customEnd.getMonth(), 1);
          }
        } else {
          // Fallback à 12 mois si pas de date spécifiée
          startMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        }
        break;
      default: // 'all' ou autre
        // 12 derniers mois par défaut
        startMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        break;
    }
    
    // Initialiser les mois concernés avec 0
    let currentMonth = new Date(startMonth);
    while (currentMonth <= endMonth) {
      const monthKey = currentMonth.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      monthlyData[monthKey] = 0;
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    // Calculer le CA pour chaque mois
    collabs.forEach(collab => {
      // Ne compter que les collaborations avec devis envoyé
      if (!collab.quote_sent) return;
      
      // Extraire la date de la collaboration et s'assurer qu'elle est valide
      let collabDate: Date;
      if (typeof collab.date === 'string') {
        collabDate = new Date(collab.date);
      } else if (collab.date && 'seconds' in collab.date) {
        collabDate = new Date(collab.date.seconds * 1000);
      } else if (collab.date) {
        collabDate = new Date(collab.date);
      } else {
        return; // Ignorer les collaborations sans date
      }

      // Formater la clé du mois de la même façon que l'initialisation
      const monthKey = collabDate.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });

      // Ajouter le CA au mois correspondant s'il existe
      if (monthKey in monthlyData) {
        const revenue = collab.influencers?.reduce((sum, inf) => 
          sum + parseFloat(inf.rate || '0'), 0) || 0;
        
        // S'assurer que la valeur est un nombre valide
        if (!isNaN(revenue)) {
          monthlyData[monthKey] += revenue;
        }
      }
    });

    // Convertir en tableau pour l'affichage et trier par date
    return Object.entries(monthlyData)
      .map(([key, value]) => ({ month: key, revenue: value }))
      // Trier chronologiquement par date
      .sort((a, b) => {
        const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
        const [monthA, yearA] = a.month.split(' ');
        const [monthB, yearB] = b.month.split(' ');
        
        const yearDiff = parseInt(yearA) - parseInt(yearB);
        if (yearDiff !== 0) return yearDiff;
        
        return months.indexOf(monthA) - months.indexOf(monthB);
      });
  };

  const monthlyRevenue = getMonthlyRevenue();
  const maxRevenue = Math.max(...monthlyRevenue.map(item => item.revenue), 1);

  // Calculer le CA total de toutes les données affichées dans le graphique
  const totalRevenue = monthlyRevenue.reduce((sum, item) => sum + item.revenue, 0);
  
  // Formater le nombre pour l'affichage
  const formattedTotalRevenue = new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    maximumFractionDigits: 0 
  }).format(totalRevenue);

  return (
    <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
        <div>
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title="Somme de toutes les périodes affichées">
            Chiffre d'affaires total
          </h2>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Basé sur les collaborations avec devis envoyé
          </p>
        </div>
        <div className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-100'}`}>
          <span 
            className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} 
            title="Chiffre d'affaires total pour la période sélectionnée"
          >
            {formattedTotalRevenue}
          </span>
        </div>
      </div>

      {/* Graphique en barres */}
      <div className="h-80 mt-8 relative">
        {/* Axe Y (montants) */}
        <div className="absolute left-0 top-0 bottom-0 w-16 flex flex-col justify-between">
          {[0, 1, 2, 3, 4].map((_, i) => (
            <div key={i} className="flex items-center">
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(maxRevenue * (4 - i) / 4)}
              </span>
            </div>
          ))}
        </div>

        {/* Lignes horizontales de la grille */}
        <div className="absolute left-16 right-0 top-0 bottom-0 flex flex-col justify-between">
          {[0, 1, 2, 3, 4].map((_, i) => (
            <div 
              key={i} 
              className={`border-b ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'} w-full h-0`}
            />
          ))}
        </div>

        {/* Barres du graphique */}
        <div className="absolute left-16 right-0 top-0 bottom-0 flex items-end">
          <div className="w-full h-full flex items-end justify-between pb-6">
            {monthlyRevenue.map((item, index) => (
              <div key={index} className="flex-1 flex flex-col items-center justify-end h-full px-1">
                <div 
                  className={`w-full max-w-[30px] mx-auto ${theme === 'dark' ? 'bg-accent-blue hover:bg-accent-blue/80' : 'bg-indigo-600 hover:bg-indigo-500'} rounded-t-md transition-all duration-200 relative group cursor-pointer`}
                  style={{ 
                    height: maxRevenue > 0 ? `${Math.max((item.revenue / maxRevenue) * 100, 2)}%` : '2px',
                    position: 'absolute',
                    bottom: '0'
                  }}
                >
                  <div className={`absolute -top-10 left-1/2 transform -translate-x-1/2 ${theme === 'dark' ? 'bg-dark-200 text-white' : 'bg-gray-800 text-white'} px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10`}>
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.revenue || 0)}
                  </div>
                </div>
                <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} whitespace-nowrap transform -rotate-45 origin-top-left translate-y-6 translate-x-2`}>
                  {item.month}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour les statuts des collaborations
const CollaborationStatusCards = ({ collabs }: { collabs: Collab[] }) => {
  const { theme } = useTheme();
  
  // Calculer les statuts
  const pending = collabs.filter(c => !c.quote_sent && !c.contract_signed).length;
  const quoteSent = collabs.filter(c => c.quote_sent && !c.contract_signed).length;
  const contractSigned = collabs.filter(c => c.contract_signed && !c.payment_received).length;
  const paymentReceived = collabs.filter(c => c.payment_received).length;
  
  const statusCards = [
    { 
      title: 'En attente', 
      count: pending, 
      icon: FileText, 
      color: theme === 'dark' ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600',
      description: 'Aucun devis ou contrat envoyé'
    },
    { 
      title: 'Devis envoyé', 
      count: quoteSent, 
      icon: ClipboardCheck, 
      color: theme === 'dark' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-600',
      description: 'En attente de signature'
    },
    { 
      title: 'Contrat signé', 
      count: contractSigned, 
      icon: CheckSquare, 
      color: theme === 'dark' ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-600',
      description: 'En attente de paiement'
    },
    { 
      title: 'Paiement reçu', 
      count: paymentReceived, 
      icon: Euro, 
      color: theme === 'dark' ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600',
      description: 'Collaborations terminées'
    }
  ];
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statusCards.map((card, index) => (
        <div 
          key={index} 
          className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`w-12 h-12 rounded-lg ${card.color} flex items-center justify-center`}>
              <card.icon className="w-6 h-6" />
            </div>
            <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {card.count}
            </span>
          </div>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
            {card.title}
          </h3>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {card.description}
          </p>
        </div>
      ))}
    </div>
  );
};

// Composant pour le tableau des influenceurs
const InfluencerCollaborationsTable = ({ collabs, influencers }: { collabs: Collab[], influencers: Influencer[] }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<'name' | 'count'>('count');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  
  // Calculer le nombre de collaborations par influenceur
  const getInfluencerCollaborations = () => {
    // Créer un Map pour stocker les informations des influenceurs par ID
    const influencerMap = new Map<string, { 
      id: string, 
      name: string, 
      count: number, 
      revenue: number, 
      fullNames: Set<string>,
      collabIds: Set<string> 
    }>();

    // Initialiser le Map avec les influenceurs connus de la base de données
    influencers.forEach(inf => {
      if (inf.id) {
        // Utiliser l'ID comme clé unique pour éviter les doublons
        influencerMap.set(inf.id, {
          id: inf.id,
          name: inf.fullName,
          count: 0,
          revenue: 0,
          fullNames: new Set([inf.fullName.toLowerCase().trim()]),
          collabIds: new Set()
        });
      }
    });
    
    // Créer un Map pour associer les noms aux IDs
    const nameToIdMap = new Map<string, string>();
    influencers.forEach(inf => {
      if (inf.id && inf.fullName) {
        nameToIdMap.set(inf.fullName.toLowerCase().trim(), inf.id);
      }
    });
    
    // Première passe: compter les collaborations avec des IDs d'influenceurs
    collabs.forEach(collab => {
      // Ne compter que les collaborations avec devis envoyé
      if (!collab.quote_sent) return;
      
      collab.influencers.forEach(inf => {
        // Ignorer les entrées sans nom ou sans ID
        if (!inf.fullName?.trim()) {
          return;
        }
        
        const normalizedName = inf.fullName.trim().toLowerCase();
        let influencerId = inf.id;
        
        // Si pas d'ID mais qu'on connaît ce nom
        if (!influencerId && nameToIdMap.has(normalizedName)) {
          influencerId = nameToIdMap.get(normalizedName);
        }
        
        // Si on a un ID valide, mettre à jour les statistiques
        if (influencerId && influencerMap.has(influencerId)) {
          const data = influencerMap.get(influencerId)!;
          // Éviter de compter deux fois la même collaboration
          if (!data.collabIds.has(collab.id!)) {
            data.count += 1;
            data.collabIds.add(collab.id!);
          }
          const rate = parseFloat(inf.rate || '0');
          if (!isNaN(rate)) {
            data.revenue += rate;
          }
          data.fullNames.add(normalizedName);
        }
      });
    });
    
    // Deuxième passe: traiter les collaborations sans ID d'influenceur
    collabs.forEach(collab => {
      // Ne compter que les collaborations avec devis envoyé
      if (!collab.quote_sent) return;
      
      collab.influencers.forEach(inf => {
        if (!inf.fullName?.trim() || inf.id) {
          return; // Ignorer si pas de nom ou si déjà traité (avec ID)
        }
        
        const normalizedName = inf.fullName.trim().toLowerCase();
        let matched = false;
        
        // Chercher si ce nom correspond à un influenceur connu
        for (const [id, data] of influencerMap.entries()) {
          if (data.fullNames.has(normalizedName)) {
            // Ce nom est déjà associé à cet influenceur
            matched = true;
            break;
          }
          
          // Vérifier les correspondances partielles
          for (const knownName of data.fullNames) {
            if (normalizedName.includes(knownName) || knownName.includes(normalizedName)) {
              // Ajouter ce nom à l'ensemble des noms connus pour cet influenceur
              data.fullNames.add(normalizedName);                
              // Éviter de compter deux fois la même collaboration
              if (!data.collabIds.has(collab.id!)) {
                data.count += 1;
                data.collabIds.add(collab.id!);
              }
              const rate = parseFloat(inf.rate || '0');
              if (!isNaN(rate)) {
                data.revenue += rate;
              }
              matched = true;
              break;
            }
          }
          
          if (matched) break;
        }
        
        // Si aucune correspondance trouvée, créer une nouvelle entrée
        if (!matched) {
          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          influencerMap.set(tempId, {
            id: tempId,
            name: inf.fullName.trim(),
            count: 1,
            collabIds: new Set([collab.id!]),
            revenue: isNaN(parseFloat(inf.rate || '0')) ? 0 : parseFloat(inf.rate || '0'),
            fullNames: new Set([normalizedName])
          });
        }
      });
    });
    
    // Convertir le Map en tableau pour le tri et le filtrage
    return Array.from(influencerMap.values())
      // Filtrer les entrées sans collaborations
      .filter(item => item.count > 0)
      .filter(item => item.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        if (sortField === 'name') {
          return sortDirection === 'asc' 
            ? a.name.localeCompare(b.name) 
            : b.name.localeCompare(a.name);
        } else {
          return sortDirection === 'asc' 
            ? a.count - b.count 
            : b.count - a.count;
        }
      });
  };
  
  const influencerCollaborations = getInfluencerCollaborations();
  
  const toggleSort = (field: 'name' | 'count') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  return (
    <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Collaborations par influenceur
        </h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un influenceur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-10 pr-4 py-2 ${theme === 'dark' ? 'bg-dark-100 border-dark-200 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} rounded-lg border`}
          />
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'}`}>
              <th 
                className={`text-left py-3 px-4 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} cursor-pointer`}
                onClick={() => toggleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <span>Influenceur</span>
                  {sortField === 'name' && (
                    sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th 
                className={`text-left py-3 px-4 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} cursor-pointer`}
                onClick={() => toggleSort('count')}
              >
                <div className="flex items-center gap-2">
                  <span>Collaborations</span>
                  {sortField === 'count' && (
                    sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th className={`text-left py-3 px-4 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                CA généré
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${theme === 'dark' ? 'divide-dark-200' : 'divide-gray-200'}`}>
            {influencerCollaborations.map((item, index) => (
              <tr 
                key={index} 
                className={`${theme === 'dark' ? 'hover:bg-dark-100/50' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
              >
                <td className={`py-4 px-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-dark-200' : 'bg-gray-100'} flex items-center justify-center`}>
                      <UserCheck className={`w-4 h-4 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
                    </div>
                    <span>{item.name}</span>
                  </div>
                </td>
                <td className={`py-4 px-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${theme === 'dark' ? 'bg-dark-200 text-white' : 'bg-gray-100 text-gray-800'}`}>
                      {item.count}
                    </span>
                  </div>
                </td>
                <td className={`py-4 px-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'} font-medium`}>
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {influencerCollaborations.length === 0 && (
          <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun influenceur trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { theme } = useTheme();
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'current' | 'all' | '3months' | 'custom'>('current');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filteredCollabs, setFilteredCollabs] = useState<Collab[]>([]);
  
  // Effet pour filtrer les collaborations quand les données ou le filtre changent
  useEffect(() => {
    loadData();
  }, []);
  
  // Effet pour filtrer les collaborations quand les données ou le filtre changent
  useEffect(() => {
    if (collabs.length > 0) {
      setFilteredCollabs(getFilteredCollabs());
    }
  }, [collabs, timeRange]);
  
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [collabsData, influencersData] = await Promise.all([
        getDocuments<Collab>(collabsRef),
        getDocuments<Influencer>(influencersRef),
      ]);
      
      setCollabs(collabsData);
      setFilteredCollabs(getFilteredCollabs(collabsData, timeRange));
      setInfluencers(influencersData.filter(inf => !inf.archived));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filtrer les données selon la plage de temps sélectionnée
  const getFilteredCollabs = (data = collabs, range = timeRange) => {
    if (range === 'all') return data;

    const now = new Date();
    let cutoffDate: Date;
    
    switch (range) {
      case 'current':
        // Début du mois actuel
        cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case '3months':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case 'custom':
        if (!startDate) return data;
        cutoffDate = new Date(startDate);
        break;
      default:
        return data;
    }
    
    // Filtrer par date de début
    let filtered = data.filter(collab => {
      let collabDate: Date;
      if (typeof collab.date === 'string') {
        collabDate = new Date(collab.date);
      } else if (collab.date && 'seconds' in collab.date) {
        collabDate = new Date(collab.date.seconds * 1000);
      } else if (collab.date) {
        collabDate = new Date(collab.date);
      } else {
        return false; // Ignorer les collaborations sans date
      }
      
      return collabDate >= cutoffDate;
    });
    
    // Si une date de fin est spécifiée pour le filtre personnalisé
    if (range === 'custom' && endDate) {
      const endCutoff = new Date(endDate);
      endCutoff.setHours(23, 59, 59); // Fin de la journée
      
      filtered = filtered.filter(collab => {
        let collabDate: Date;
        if (typeof collab.date === 'string') {
          collabDate = new Date(collab.date);
        } else if (collab.date && 'seconds' in collab.date) {
          collabDate = new Date(collab.date.seconds * 1000);
        } else if (collab.date) {
          collabDate = new Date(collab.date);
        } else {
          return false;
        }
        
        return collabDate <= endCutoff;
      });
    }
    
    return filtered;
  };
  
  // Calculer le CA total
  const getTotalRevenue = () => {
    return collabs.reduce((total, collab) => {
      const collabTotal = collab.influencers?.reduce((sum, inf) => 
        {
          const rate = parseFloat(inf.rate || '0');
          return sum + (isNaN(rate) ? 0 : rate);
        }, 0) || 0;
      return total + collabTotal;
    }, 0);
  };
  
  // Formater la date pour l'affichage
  const formatDateRange = () => {
    const now = new Date();
    
    switch (timeRange) {
      case 'current':
        return `Mois de ${now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`;
      case '3months':
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        return `${threeMonthsAgo.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - ${now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
      case 'custom':
        if (!startDate) return 'Période personnalisée';
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : now;
        return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
      default:
        return 'Toutes les périodes';
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Dashboard
            </h1>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Suivi global de l'activité
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsCalendarModalOpen(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-black ${
              theme === 'dark' 
                ? 'bg-dark-100 text-white hover:bg-dark-200' 
                : 'bg-white text-gray-900 hover:bg-gray-50'
            } transition-colors`}
          >
            <Filter className="w-4 h-4" />
            <span>{formatDateRange()}</span>
          </button>
        </div>
      </div>
      
      {error && (
        <div className={`p-4 rounded-lg ${
          theme === 'dark' 
            ? 'bg-red-900/50 border-red-500/20 text-red-200' 
            : 'bg-red-50 border-red-200 text-red-800'
        } border`}>
          {error}
        </div>
      )}
      
      {/* Statuts des collaborations */}
      <CollaborationStatusCards collabs={filteredCollabs} />
      
      {/* Graphique de CA mensuel */}
      <MonthlyRevenueChart collabs={filteredCollabs} timeRange={timeRange} startDate={startDate} endDate={endDate} />
      
      {/* Tableau des influenceurs */}
      <InfluencerCollaborationsTable collabs={filteredCollabs} influencers={influencers} />
      
      {/* Modal de sélection de période */}
      <Dialog
        open={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border shadow-lg`}>
            <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
              Filtrer par période
            </h2>
            
            <div className="space-y-4">
              <button
                onClick={() => {
                  setTimeRange('current');
                  setIsCalendarModalOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 border-black ${
                  timeRange === 'current' 
                    ? theme === 'dark' ? 'bg-accent-blue text-white' : 'bg-indigo-600 text-white'
                    : theme === 'dark' ? 'bg-dark-100 text-white hover:bg-dark-200' : 'bg-white text-gray-900 hover:bg-gray-50'
                } transition-colors`}
              >
                Mois actuel
              </button>
              
              <button
                onClick={() => {
                  setTimeRange('3months');
                  setIsCalendarModalOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 border-black ${
                  timeRange === '3months' 
                    ? theme === 'dark' ? 'bg-accent-blue text-white' : 'bg-indigo-600 text-white'
                    : theme === 'dark' ? 'bg-dark-100 text-white hover:bg-dark-200' : 'bg-white text-gray-900 hover:bg-gray-50'
                } transition-colors`}
              >
                3 derniers mois
              </button>
              
              <button
                onClick={() => {
                  setTimeRange('all');
                  setIsCalendarModalOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 border-black ${
                  timeRange === 'all' 
                    ? theme === 'dark' ? 'bg-accent-blue text-white' : 'bg-indigo-600 text-white'
                    : theme === 'dark' ? 'bg-dark-100 text-white hover:bg-dark-200' : 'bg-white text-gray-900 hover:bg-gray-50'
                } transition-colors`}
              >
                Toutes les périodes
              </button>
              
              <div className="border-t-2 border-black pt-4">
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
                  Période personnalisée
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Date de début
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border-2 border-black ${
                        theme === 'dark' ? 'bg-dark-100 text-white' : 'bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Date de fin
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border-2 border-black ${
                        theme === 'dark' ? 'bg-dark-100 text-white' : 'bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  
                  <button
                    onClick={() => {
                      if (startDate) {
                        setTimeRange('custom');
                        setIsCalendarModalOpen(false);
                      }
                    }}
                    disabled={!startDate}
                    className={`w-full px-4 py-3 rounded-lg border-2 border-black ${
                      !startDate
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : timeRange === 'custom'
                          ? theme === 'dark' ? 'bg-accent-blue text-white' : 'bg-indigo-600 text-white'
                          : theme === 'dark' ? 'bg-dark-100 text-white hover:bg-dark-200' : 'bg-white text-gray-900 hover:bg-gray-50'
                    } transition-colors`}
                  >
                    Appliquer la période personnalisée
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}